import { currencyFormatter, mergeTags } from 'shared';
import { useMemo } from 'react';

import { env } from '../../common/lib/env';

import { useGetSelectedOrganisation } from './useGetSelectedOrganisation';
import { useIsUsingDisplayName } from './useSettings';

export function useMergeTags(type: keyof typeof mergeTags) {
  const currentOrg = useGetSelectedOrganisation();
  const useDisplayName = useIsUsingDisplayName();

  const result = useMemo(() => {
    if (!type || !currentOrg) return {
      asArray: [],
      asTestData: {},
    };

    const asTestData = {};
    const obj = { ...mergeTags[type] };
    const formatCurrency = currencyFormatter(currentOrg.baseCurrency);

    Object.keys(obj).forEach(key => {
      if (obj[key].devOnly && !env.isDev) {
        delete obj[key];

        return; // skip this key
      }

      if (obj[key].appliesToAccounting && !obj[key].appliesToAccounting.includes(currentOrg.type)) {
        delete obj[key];

        return; // skip this key
      }

      if (obj[key].requiresPaymentProvider) {
        const currentProviders = currentOrg.paymentProviders.map(p => p.type);

        if (!currentProviders.includes(obj[key].requiresPaymentProvider)) {
          delete obj[key];

          return; // skip this key
        }
      }

      let sample = obj[key].sample;

      if (obj[key].isCurrency && sample) {
        const amount = Number(String(sample).replace(/[^0-9.-]/g, ``));

        if (!Number.isNaN(amount)) {
          sample = formatCurrency(amount);
        }
      }

      if (obj[key].value === `OrganisationName`) {
        if (useDisplayName) {
          sample = currentOrg.displayName || currentOrg.legalName;
        }
        else {
          sample = currentOrg.legalName;
        }
      }

      asTestData[obj[key].value] = sample;
    });

    const asArray = Object.values(obj);

    return {
      asArray,
      asTestData,
    };
  }, [currentOrg, type, useDisplayName]);

  return result;
}
